import { createTheme } from '@mui/material/styles';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import createCache from '@emotion/cache';


export const createPath = (width=1024, height=740, cornerRadius=20, notchHeight=90, notchWidth=220) => {

  const radius = cornerRadius;
  // Calculate scaled coordinates while maintaining proportions
  const coords = {
    topLeft: { x: cornerRadius, y: 0 },
    topRight: { x: width - cornerRadius, y: 0 },
    rightTop: { x: width, y: cornerRadius },
    rightBottom: { x: width, y: height - cornerRadius },
    bottomRight: { x: width - cornerRadius, y: height },
    notchStart: { x: notchWidth + cornerRadius, y: height },
    notchTop: { x: notchWidth, y: height - cornerRadius },
    notchMid: { x: notchWidth, y: height - notchHeight + radius },
    notchCorner: { x: notchWidth, y: height - notchHeight },
    leftNotch: { x: cornerRadius, y: height - notchHeight },
    leftBottom: { x: 0, y: height - notchHeight- cornerRadius },
    leftTop: { x: 0, y: cornerRadius }
  };


// Construct the path string
  const formClipPathRows = `M ${coords.topLeft.x},${coords.topLeft.y} 
    L ${coords.topRight.x},${coords.topRight.y} 
    A ${radius},${radius} 0,0,1 ${coords.rightTop.x},${coords.rightTop.y} 
    L ${coords.rightBottom.x},${coords.rightBottom.y} 
    A ${radius},${radius} 0,0,1 ${coords.bottomRight.x},${coords.bottomRight.y} 
    L ${coords.notchStart.x},${coords.notchStart.y} 
    A ${radius},${radius} 0,0,1 ${coords.notchTop.x},${coords.notchTop.y} 
    L ${coords.notchMid.x},${coords.notchMid.y} 
    A ${radius},${radius} 0,0,0 ${coords.notchCorner.x - radius},${coords.notchCorner.y}   
    L ${coords.leftNotch.x},${coords.leftNotch.y} 
    A ${radius},${radius} 0,0,1 ${coords.leftBottom.x},${coords.leftBottom.y} 
    L ${coords.leftTop.x},${coords.leftTop.y} 
    A ${radius},${radius} 0,0,1 ${coords.topLeft.x},${coords.topLeft.y} 
    Z`.replace(/\n+/g, '').trim();

  return `path("${formClipPathRows}")`
}


// Create rtl cache
export const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

//TODO here just to use breakpoints (find a better way)
const defaultTheme = createTheme();

const theme = createTheme({
  direction: 'rtl',
  palette: {
    primary: {
      main: '#7544E1',
      light: '#9F7AEA',
      dark: '#553C9A',
      blue: '#2E63EE',
    },
    secondary: {
      main: '#38B2AC',
      light: '#4FD1C5',
      dark: '#2C7A7B',
      blue: '#2E63EE',
    },
    background: {
      default: '#F7FAFC',
      paper: '#FFFFFF',
      lightBlue: '#F1F5FF',
      lightGray: '#f5f5f5',
    },
    text: {
      primary: '#29286A',
      question: '#1D094C',
      answer: '#3E3C9F',
      inputLabel: '#3E3C9F',
      error: '#d32f2f',
    },
    border: {
      main: '#C4C4C4',
      hover: '#3E3C9F',
      light: '#E6E6F2',
    },
    button: {
      primary: '#7544E1',
      gradient: {
        start: '#7A40E2',
        end: '#3866E5',
      },
      hover: {
        start: '#8533EB',
        end: '#4001BF',
      },
    },
    icon: {
      expanded: '#7544E1',
      collapsed: '#9E9DCF',
    },
    brands: {
      primary: '#3E3C9F',
      text: {
        active: '#1F1E4F',
        inactive: '#7E7DBF',
      },
      background: {
        default: '#FFFFFF',
        icon: '#FFFFFF',
      },
      shadow: {
        icon: '0px 4.62px 69.23px rgba(45, 85, 199, 0.08)',
        active: '0px 4px 20px 0px rgba(4, 104, 201, 0.16)',
      },
    },
    common: {
      white: '#FFFFFF',
      shadow: {
        default: '0px 4.62px 69.23px rgba(45, 85, 199, 0.08)',
      },
    },
    serviceCard: {
      text: '#1F1E4F',
      background: theme => theme.palette.common.white,
      shadow: theme => theme.palette.common.shadow.default,
    },
    savingsSection: {
      title: '#3E3C9F',
      subtitle: '#7E7DBF',
      amount: '#4001BF',
      slider: '#D8D8EC',
      sliderThumb: '#7443E2',
      sliderRail: '#29286A',
      sliderTrack: '#7443E2',
      sliderTrackHover: '#5351BD',
    },
    doughnutChart: {
      primary: '#3E3C9F',
      secondary: '#7E7DBF',
      accent: '#4001BF',
      background: '#F1F5FF',
      text: '#29286A',
      hover: '#5351BD',
    },
    benefitsSection: {
      title: '#3E3C9F',
      subtitle: '#7E7DBF',
      background: '#F1F5FF',
      text: '#29286A',
      hover: '#5351BD',
    },
    formSection: {
      title: '#29286A',
      subtitle: '#5351BD',
    },
    howToJoinSection: {
      cardContentHeader: '#1D094C',
      joinButton: '#7544E1',
      
    },
    
  },
  typography: {
    fontFamily: '"OpenSansHebrew-Regular", sans-serif',
    fontFamilyBold: '"OpenSansHebrew-Bold", sans-serif',
    fontFamilyExtraBold: '"OpenSansHebrew-ExtraBold", sans-serif',
    scorpion: '"Fb Scorpion", sans-serif',
    richText: {
      fontSize: '22px',
      fontWeight: 400,
      lineHeight: '1.5',
      textAlign: 'left',
      display: 'block',
      
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '24px',
      },
      [defaultTheme.breakpoints.up('md')]: {
        fontSize: '32px',
      },
    },
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
    title: {
      fontSize: "24px",
      fontWeight: 700,
      lineHeight: "1.08",
      textAlign: "left",
      display: "block",
     },
     subtitle: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "1.08",
      textAlign: "left",
      display: "block",
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '18px',
      },
     },
     inputLabel: {
      color: '#3E3C9F',
      fontSize: '14px',
        [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '18px',
      },
     },
     sectionTitle: {
      fontSize: '24px',
      [defaultTheme.breakpoints.up('sm')]: {
        fontSize: '36px',
      },
     },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '24px',
          textTransform: 'none',
          fontWeight: 600,
        }
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            },
          },
      },
    },
  },
});

export default theme;
